import {useCallback, useEffect, useState} from 'react';
import {Button, Checkbox, FormControlLabel, Grid, makeStyles, TextField, withStyles} from "@material-ui/core";
import CreatorBtn from "../../../../atoms/creator/experienceBuilderEnterBtn";
import DateFnsUtils from '@date-io/date-fns';
import {DatePicker, KeyboardTimePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {useDispatch, useSelector} from "react-redux";
import {
  moveNextPage,
  updateUserMetrics,
  validateSessionCode
} from "../../../../../store/actions/creator/creatorExperienceBuilder";
import UtilService from '../../../../../helper/service';
import {apiClient} from '../../../../../config/apiClient';
import moment from "moment";
import CustomSnackBar from '../../experienceManager/participantsDashboard/component/CustomSnackBar';
import clsx from 'clsx';
import {useHistory, useLocation} from "react-router";

const useStyles = makeStyles(() => ({
  mainTitle: {
    fontSize: 48,
    fontFamily: 'Spartan-bold',
    color: '#212D40',
    textAlign: 'center',
    marginTop: -10,
    lineHeight: '55px',
    '@media only screen and (max-width: 600px)': {
      fontSize: 20
    }
  },
  bottomText: {
    textAlign: 'right',
    fontSize: 10,
    fontFamily: 'roboto',
    marginTop: 0
  },
  enter: {
    color: '#212D40',
    fontFamily: 'roboto-bold'
  },
  label: {
    "&$focusedLabel": {
      color: "blue"
    },
    "&$erroredLabel": {
      color: "green"
    }
  },
  focusedLabel: {
    fontSize: 14,
    fontFamily: 'roboto-black',
  },
  erroredLabel: {},
  underline: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#8F99A3',
  },
  normal: {
    color: '#8F99A3',
    "&:before": {
      borderBottom: `1px solid rgba(0, 0, 0, 0.42)`
    },
    "&:after": {
      borderBottom: `1px solid rgba(0, 0, 0, 0.42)`
    }
  },
  error: {
    color: '#f44336',
    "&:before": {
      borderBottom: `1px solid #f44336`
    },
    "&:after": {
      borderBottom: `1px solid #f44336`
    }
  },
  errMsg: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#f44336',
    marginTop: '5px',
    "&:before": {
      borderBottom: `1px solid #f44336`
    },
    "&:after": {
      borderBottom: `1px solid #f44336`
    }
  },
  warning: {
    fontSize: 14,
    fontFamily: 'roboto-regular',
    color: '#F7B926',
    marginTop: '5px',
    "&:before": {
      borderBottom: `1px solid #f44336`
    },
    "&:after": {
      borderBottom: `1px solid #f44336`
    }
  },
  title: {
    color: '#212D40',
    fontSize: 24,
    fontFamily: 'roboto-bold',
    marginBottom: -30,
    marginTop: 50
  },
  checkbox: {
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  calendarBtnLabel: {
    fontSize: 16,
    fontFamily: 'roboto-regular',
    textTransform: 'none'
  },
  checkBoxLabel: {
    color: '#212D40',
    fontFamily: 'roboto-regular',
    fontSize: 14
  },
  checkedLabel: {
    fontWeight: 'bold'
  },
  clockIcon: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
    background: 'white'
  },
  timeDiv: {
    display: 'flex',
  },
  timePickerInput: {
    width: '160px!important',
    '& > div': {
      fontSize: 14,
      fontFamily: 'roboto-regular',
      color: '#8F99A3'
    },
    '& input': {
      display: 'flex',
      alignItems: 'center'
    }
  },
  timezoneStart: {
    '& input#timezoneStart::before': {
      content: (props: any) => props.start ? `"${props.start}"`: "''",
      marginLeft: 74,
      position: 'absolute'
    },
  },
  timezoneEnd: {
    '& input#timezoneEnd::before': {
      content: (props: any) => props.end ? `"${props.end}"`: "''",
      marginLeft: 74,
      position: 'absolute'
    },
  }
}));

const CssTextField = withStyles({
  root: {
    marginRight: 40,
    fontSize: 24,
    '& label': {
      fontSize: 14,
      color: '#697683',
      fontWeight: '700'
    },
    '& input': {
      fontSize: 14,
      fontWeight: '400',
      color: '#8F99A3',
    },
    '& .MuiInput-underline:before': {
      borderBottomColor: '#697683',
      borderBottomWidth: 1.6,
    },
    "& .MuiInputBase-root.Mui-disabled:before": {
      borderBottom: '1.6px solid #697683'
    }
  },
})(TextField);

function LiveExperienceCalendarLinkBasedInvite() {
  const { userMetrics, page, globalSaveSetting, prevPage } = useSelector((state: any) => state.creatorExperienceBuilder);
  const {
    moderatedName, modStartDateTime, modEndDateTime, sessionCode, startNow, modLastActivePage, moderatedInviteWay
  } = userMetrics;

  const classes = useStyles({
    start: UtilService.getTimeZoneDate(modStartDateTime) !== 'Invalid date' ? UtilService.getTimeZoneDate(modStartDateTime) : '', 
    end: UtilService.getTimeZoneDate(modEndDateTime) !== 'Invalid date' ? UtilService.getTimeZoneDate(modEndDateTime) : '', 
  });

  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const apolloClient = useSelector((state: any) => state.apolloClient);
  const loginUserData = localStorage.getItem("user_data") ? JSON.parse(localStorage.getItem("user_data") || "{}") : ""
  const [isShownSnackAlert, setShownSnackAlert] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [validStartDate, setValidStartDate] = useState(0);
  const [validStartTime, setValidStartTime] = useState(0);
  const [validEndDate, setValidEndDate] = useState(0);
  const [validEndTime, setValidEndTime] = useState(0);
  const [diffHours, setDiffHours] = useState(0);

  const invalidStartDateMsg = ["", "Start Date must be later than current Date & Time"];
  const invalidStartTimeMsg = ["", "Start Time must be later than current Date & Time", 'Invalid Time Format'];
  const invalidEndDateMsg = ["", "End Date must be later than Start Date & Time", "Duration must be longer than 5 minutes"];
  const invalidEndTimeMsg = ["", "End Time must be later than Start Date & Time", "", 'Invalid Time Format'];
  const warningDiffHours = 'We recommend breaking experiences up into 2 hour segments.';

  const setExperienceName = (e: string) => {
    dispatch(updateUserMetrics('moderatedName', e))
  };

  const handleUserKeyPress = (event: any) => {
    if (
      event.keyCode === 13 && isEdit && validStartDate === 0 && validStartTime === 0
      && validEndDate === 0 && validEndTime === 0
    ) {
      goNext();
    }
  };

  useEffect(() => {
    window.addEventListener('keydown', handleUserKeyPress);
    return () => {
      window.removeEventListener('keydown', handleUserKeyPress);
    };
  });
  
  useEffect(() => {
    if (history.location.hash !== '#schedule') {
      UtilService.addHashTag(history, location.pathname, 'schedule');
    }
    if (moderatedName) {
     setIsEdit(true)
    }
  }, []);

  useEffect(() => {
    let startDateTime = new Date(modStartDateTime.getTime());
    let endDateTime = new Date(modEndDateTime.getTime());

    if (modStartDateTime.getTime() < new Date().getTime()) {
      startDateTime = new Date(UtilService.getStartDateTime());
      endDateTime = new Date(UtilService.getEndDateTime(startDateTime));
      dispatch(updateUserMetrics('modStartDateTime', startDateTime));
      prevPage < 3 && dispatch(updateUserMetrics('modEndDateTime', endDateTime));
    }
    checkValidation();
  }, [dispatch]);

  useEffect(() => {
    const endDateTime = UtilService.getEndDateTime(modStartDateTime);
    prevPage < 3 && dispatch(updateUserMetrics('modEndDateTime', endDateTime));
    checkValidation();
  }, [modStartDateTime]);

  useEffect(() => {
    checkValidation();
  }, [modEndDateTime]);

  useEffect(()=>{
    const getSessionCode = async () => {
      const payload = {
        "requestor": "experience",
        "tenant_id": loginUserData.customer_id
      };
      const res = await apiClient(false).post('/registration/v2/session/tokens', payload);
      if (res && res.data && res.data.token) {
        const checkSession = await validateSessionCode(apolloClient.client, res.data.token);
        if(checkSession && checkSession.data && checkSession.data.validateSessionCode){
          localStorage.setItem("session_code", String(res.data.token));
          dispatch(updateUserMetrics('sessionCode', res.data.token));
        } else {
          getSessionCode();
        }
      } else {
        getSessionCode();
      }
    };
    if (!sessionCode) {
      getSessionCode();
    }
  },[dispatch, sessionCode, loginUserData.customer_id, apolloClient]);

  const goNext = useCallback(() => {
    const nextPage = (globalSaveSetting || moderatedInviteWay === 'hub') ? page + 2 : page + 1;
    dispatch(moveNextPage(nextPage));
    (modLastActivePage < nextPage) && dispatch(updateUserMetrics('modLastActivePage', nextPage));
  }, [globalSaveSetting, moderatedInviteWay, page, modLastActivePage]);

  const checkValidation = useCallback(() => {
    let ved = 0;
    let vet = 0;
    let vsd = 0;
    let vst = 0;

    const now = new Date();
    const date1 = new Date(modStartDateTime.getTime());
    date1.setHours(0,0,0,0);

    const date2 = new Date(modEndDateTime.getTime());
    date2.setHours(0,0,0,0);

    ved = (date2 >= date1) ? 0 : 1;
    vet = modStartDateTime.getTime() < modEndDateTime.getTime() ? 0 : 1;

    const diff_start_end_hours = diff_hours(modEndDateTime, modStartDateTime);
    setDiffHours(diff_start_end_hours); // show warning if diff hours is longer than 3

    if (modStartDateTime.getTime() < now.getTime()) {
      const date1 = new Date(modStartDateTime.getTime());
      date1.setHours(0,0,0,0);

      const date2 = new Date(now.getTime());
      date2.setHours(0,0,0,0);

      if (vsd === 0)
        vsd = (date1 > date2) ? 0 : 1;   // Start Date should be after now.
      if (vst === 0)
        vst = (date1 !== date2) ? 0 : 1;  // Start Time should be after now.
    }

    if ((modEndDateTime.getTime() - modStartDateTime.getTime()) < 5 * 60 * 1000) {
      if (ved === 0 && vet === 0)
        ved = 2;
      if (vet === 0)
        vet = 2;
    }

    setValidStartDate(vsd);
    setValidStartTime(vst);
    setValidEndDate(ved);
    setValidEndTime(vet);
  }, [modStartDateTime, modEndDateTime]);

  const diff_hours = (dt2: any, dt1: any) => {
    let diff =(dt2.getTime() - dt1.getTime()) / 1000;
    diff /= (60 * 60);
    return Math.abs(Math.round(diff));
  };

  const hideSnackAlert = () => {
    setShownSnackAlert(false);
  }

  const cancelBtn = () => {
    return (
      <Button variant="contained" className={classes.calendarBtnLabel}>
        Cancel
      </Button>
    )
  };

  const okBtn = () => {
    return (
      <Button variant="contained" color='primary' className={classes.calendarBtnLabel}>
        OK
      </Button>
    )
  };

  const onKeyPress = (e: any) => {
    if (e.key === 'Enter') {
      setIsEdit(true)
    } else if (!moderatedName) {
      setIsEdit(false)
    }
  };

  const onStartNow = (isStartNow: boolean) => {
    dispatch(updateUserMetrics('startNow', isStartNow));
    const currentTime = new Date();
    const updatedTime = new Date(currentTime.getTime() + 60 * 1000);
    dispatch(updateUserMetrics('modStartDateTime', updatedTime));
  };

  const handleStartDateChange = (startDate: Date) => {
    dispatch(updateUserMetrics('modStartDateTime', startDate));
  };

  const handleStartTimeChange = (startTime: Date) => {
    dispatch(updateUserMetrics('modStartDateTime', startTime));
  };

  const handleEndDateChange = (endDate: Date) => {
    dispatch(updateUserMetrics('modEndDateTime', endDate));
  };

  const handleEndTimeChange = (endTime: Date) => {
    dispatch(updateUserMetrics('modEndDateTime', endTime));
  };

  const getStartTimeStr = useCallback(() => {
    const time = UtilService.getLocalTime(modStartDateTime);
    const hour = time.getHours();
    const minute = time.getMinutes();
    return `${hour >= 10 ? hour : `0${hour}`}:${minute >= 10 ? minute : `0${minute}`}`
  }, [modStartDateTime]);

  const onChangeStartTime = useCallback((e: any) => {
    const timeStr = e.target.value;
    if (timeStr) {
      const hoursAndMinutes = timeStr.split(':');
      const hours = Number(hoursAndMinutes[0]);
      const minutes = Number(hoursAndMinutes[1]);

      const time = UtilService.getLocalTime(modStartDateTime);
      const updatedTime = time.setHours(hours, minutes);
      dispatch(updateUserMetrics('modStartDateTime', UtilService.getLocalTimeReverse(new Date(updatedTime))));
    }
  }, [modStartDateTime]);

  const getEndTimeStr = useCallback(() => {
    const time = UtilService.getLocalTime(modEndDateTime);
    const hour = time.getHours();
    const minute = time.getMinutes();
    return `${hour >= 10 ? hour : `0${hour}`}:${minute >= 10 ? minute : `0${minute}`}`
  }, [modEndDateTime]);

  const onChangeEndTime = useCallback((e: any) => {
    const timeStr = e.target.value;
    if (timeStr) {
      const hoursAndMinutes = timeStr.split(':');
      const hours = Number(hoursAndMinutes[0]);
      const minutes = Number(hoursAndMinutes[1]);

      const time = UtilService.getLocalTime(modEndDateTime);
      const updatedTime = time.setHours(hours, minutes);
      dispatch(updateUserMetrics('modEndDateTime', UtilService.getLocalTimeReverse(new Date(updatedTime))));
    }
  }, [modEndDateTime]);

  return (
    <div onKeyPress={onKeyPress}>
      <p className="creator-title mt-65">
        Before sending a link, we need to set up the event.
      </p>
      <p className={classes.mainTitle}>
        Name your event and <br/>
        tell us when it’s happening
      </p>

      <div className='creator-main-panel' >
        <CssTextField
          margin="normal"
          fullWidth
          id="experience_name"
          label="Experience Name"
          name="experience_name"
          value={moderatedName}
          onChange={e => setExperienceName(e.target.value.normalize ("NFKD").replace (/[\u0300-\u036F]/g, ""))}
        />
      {(!moderatedName || !isEdit)  &&<p className={classes.bottomText}>press <span className={classes.enter}>Enter</span></p>}

        {
         (moderatedName && isEdit) &&
          <div>
            <p className={classes.title}>Excellent! When is your event happening?</p>
            <p className="creator-subtitle">
              Start
            </p>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <Grid className={classes.timeDiv}>
                <DatePicker
                  value={UtilService.getLocalTime(modStartDateTime)}
                  onChange={(startDate: any) => handleStartDateChange(UtilService.getLocalTimeReverse(startDate))}
                  animateYearScrolling
                  format="MMMM dd, yyyy"
                  cancelLabel={cancelBtn()}
                  okLabel={okBtn()}
                  minDate={UtilService.getLocalTime(new Date())}
                  disabled={ startNow }
                  error={validStartTime !== 0}
                  InputLabelProps={{
                    classes: {
                      root: clsx(classes.label, validStartDate === 0 ? classes.normal : classes.error),
                      focused: classes.focusedLabel,
                      error: classes.erroredLabel
                    },
                  }}
                  InputProps={{
                    classes: {
                      root: clsx(classes.underline, validStartDate === 0 ? classes.normal : classes.error),
                      error: classes.error
                    },
                    value: moment(UtilService.getLocalTime(modStartDateTime)).format('MMMM DD, yyyy')
                  }}
                />
                &nbsp;&nbsp;&nbsp;
                <TextField
                  type="time"
                  value={getStartTimeStr()}
                  className={classes.timePickerInput}
                  disabled={ startNow }
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    step: 300, // 5 min
                  }}
                  onChange={(e: any) => onChangeStartTime(e)}
                  id='timezoneStart'
                  InputProps={{
                    className: classes.timezoneStart,
                  }}
                />
                <KeyboardTimePicker
                  id="start-time-picker"
                  value={UtilService.getLocalTime(modStartDateTime)}
                  disabled={ startNow }
                  cancelLabel={cancelBtn()}
                  okLabel={okBtn()}
                  error={validStartTime !== 0}
                  onChange={(startTime: any) => handleStartTimeChange(UtilService.getLocalTimeReverse(startTime))}
                  KeyboardButtonProps={{
                    'aria-label': 'change time',
                  }}
                  style={{ width: 0, marginLeft: '-40px' }}
                  keyboardIcon={
                    <img
                      className={classes.clockIcon}
                      src={`/assets/icons/experiencebuilder/${startNow ? 'disabled-clock' : 'clock'}.svg`}
                      alt={'Clock'}
                    />
                  }
                  InputLabelProps={{
                    classes: {
                      root: clsx(classes.label, validStartTime === 0 ? classes.normal : classes.error),
                      focused: classes.focusedLabel,
                      error: classes.erroredLabel
                    }
                  }}
                  InputProps={{
                    classes: {
                      root: clsx(classes.underline, validStartTime === 0 ? classes.normal : classes.error),
                      error: classes.error
                    }
                  }}
                />
              </Grid>
              <div className={classes.errMsg}>{ invalidStartDateMsg[validStartDate] }</div>
              <div className={classes.errMsg}>{ invalidStartTimeMsg[validStartTime] }</div>
              <FormControlLabel
                control={
                  <Checkbox
                    className={classes.checkbox}
                    checked={startNow}
                    onChange={(e) => onStartNow(e.target.checked)}
                    name="start_now"
                    color="default"
                  />
                }
                label={
                  <span className={clsx(classes.checkBoxLabel, startNow && classes.checkedLabel)}>
                    Start Now
                  </span>
                }
              />
            </MuiPickersUtilsProvider>

            <p className="creator-subtitle" style={{ marginTop: 40 }}>
              <span>End</span> <span className='creator-description'></span>
            </p>
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <Grid className={classes.timeDiv}>
                <DatePicker
                  value={UtilService.getLocalTime(modEndDateTime)}
                  onChange={(endDate: any) => handleEndDateChange(UtilService.getLocalTimeReverse(endDate))}
                  animateYearScrolling
                  format="MMMM dd, yyyy"
                  cancelLabel={cancelBtn()}
                  okLabel={okBtn()}
                  minDate={UtilService.getLocalTime(modStartDateTime)}
                  error={validEndDate !== 0}
                  InputLabelProps={{
                    classes: {
                      root: classes.label,
                      focused: classes.focusedLabel,
                      error: classes.erroredLabel
                    },
                  }}
                  InputProps={{
                    classes: {
                      root: clsx(classes.underline, validEndDate === 0 ? classes.normal : classes.error),
                      error: classes.error
                    },
                    value: moment(UtilService.getLocalTime(modEndDateTime)).format('MMMM DD, yyyy')
                  }}
                />
                &nbsp;&nbsp;&nbsp;
                <TextField
                  type="time"
                  value={getEndTimeStr()}
                  className={classes.timePickerInput}
                  onChange={(e: any) => onChangeEndTime(e)}
                  InputLabelProps={{
                    shrink: true
                  }}
                  inputProps={{
                    step: 300, // 5 min
                  }}
                  id='timezoneEnd'
                  InputProps={{
                    className: classes.timezoneEnd,
                  }}
                />
                <KeyboardTimePicker
                  id="end-time-picker"
                  value={UtilService.getLocalTime(modEndDateTime)}
                  cancelLabel={cancelBtn()}
                  okLabel={okBtn()}
                  error={validEndTime !== 0}
                  onChange={(endTime: any) => handleEndTimeChange(UtilService.getLocalTimeReverse(endTime))}
                  KeyboardButtonProps={{
                    'aria-label': 'change time',
                  }}
                  style={{ width: 0, marginLeft: '-40px' }}
                  keyboardIcon={
                    <img
                      className={classes.clockIcon}
                      src={'/assets/icons/experiencebuilder/clock.svg'}
                      alt={'Clock'}
                    />
                  }
                  InputLabelProps={{
                    classes: {
                      root: clsx(classes.label, validEndTime === 0 ? classes.normal : classes.error),
                      focused: classes.focusedLabel,
                      error: classes.erroredLabel
                    }
                  }}
                  InputProps={{
                    classes: {
                      root: clsx(classes.underline, validEndTime === 0 ? classes.normal : classes.error),
                      error: classes.error
                    }
                  }}
                />
              </Grid>
              <div className={classes.errMsg}>{ invalidEndDateMsg[validEndDate] }</div>
              <div className={classes.errMsg}>{ invalidEndTimeMsg[validEndTime] }</div>
              {diffHours >= 3 && (
                <div className={classes.warning}>{ warningDiffHours }</div>
              )}
            </MuiPickersUtilsProvider>

          </div>
        }

      </div>

      {moderatedName && isEdit &&  <CreatorBtn
        isValidation={true}
        isEnabled={validStartDate === 0 && validStartTime === 0 && validEndDate === 0 && validEndTime === 0}
        goNext={goNext}
      />}

      <br /><br />
      <CustomSnackBar type={"warning"} message={''} open={isShownSnackAlert} onClose={hideSnackAlert} />
    </div>
  )
}

export default LiveExperienceCalendarLinkBasedInvite;